import { a as attr, e as emptyArray, D as DOM } from './vivid-element.js';
import { k as keyEnter } from './key-codes.js';

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
const proxySlotName = "form-associated-proxy";
const ElementInternalsKey = "ElementInternals";
const supportsElementInternals = ElementInternalsKey in window && "setFormValue" in window[ElementInternalsKey].prototype;
const InternalsMap = /* @__PURE__ */ new WeakMap();
const FormAssociated = (Base) => {
  class FormAssociatedElement extends Base {
    constructor(...args) {
      super(...args);
      /**
       * Track whether the value has been changed from the initial value
       * @internal
       */
      this.dirtyValue = false;
      this.disabled = false;
      /**
       * These are events that are still fired by the proxy
       * element based on user / programmatic interaction.
       *
       * The proxy implementation should be transparent to
       * the app author, so block these events from emitting.
       * @internal
       */
      this.proxyEventsToBlock = ["change", "click"];
      /**
       * @internal
       */
      this.proxyInitialized = false;
      this.required = false;
      this.initialValue = this.initialValue || "";
      if (!this.elementInternals) {
        this.formResetCallback = this.formResetCallback.bind(this);
      }
    }
    /**
     * Must evaluate to true to enable elementInternals.
     * Feature detects API support and resolve respectively
     *
     * @internal
     */
    static get formAssociated() {
      return supportsElementInternals;
    }
    /**
     * Returns the validity state of the element
     */
    get validity() {
      return this.elementInternals ? this.elementInternals.validity : this.proxy.validity;
    }
    /**
     * Retrieve a reference to the associated form.
     * Returns null if not associated to any form.
     */
    get form() {
      return this.elementInternals ? this.elementInternals.form : this.proxy.form;
    }
    /**
     * Retrieve the localized validation message,
     * or custom validation message if set.
     */
    get validationMessage() {
      return this.elementInternals ? this.elementInternals.validationMessage : this.proxy.validationMessage;
    }
    /**
     * Whether the element will be validated when the
     * form is submitted
     */
    get willValidate() {
      return this.elementInternals ? this.elementInternals.willValidate : this.proxy.willValidate;
    }
    /**
     * A reference to all associated label elements
     */
    get labels() {
      if (this.elementInternals) {
        return Object.freeze(Array.from(this.elementInternals.labels));
      } else if (this.proxy instanceof HTMLElement && this.proxy.ownerDocument && this.id) {
        const parentLabels = this.proxy.labels;
        const forLabels = Array.from(
          this.proxy.getRootNode().querySelectorAll(`[for='${this.id}']`)
        );
        return Object.freeze(forLabels.concat(Array.from(parentLabels)));
      } else {
        return emptyArray;
      }
    }
    /**
     * @internal
     */
    valueChanged(_previous, _next) {
      this.dirtyValue = true;
      if (this.proxy instanceof HTMLElement) {
        this.proxy.value = this.value;
      }
      this.setFormValue(this.value);
      this.validate();
    }
    /**
     * @deprecated Use `value` instead.
     */
    get currentValue() {
      return this.value;
    }
    set currentValue(value) {
      this.value = value;
    }
    /**
     * @internal
     */
    initialValueChanged(_previous, _next) {
      if (!this.dirtyValue) {
        this.value = this.initialValue;
        this.dirtyValue = false;
      }
    }
    /**
     * @internal
     */
    disabledChanged(_previous, _next) {
      if (this.proxy instanceof HTMLElement) {
        this.proxy.disabled = this.disabled;
      }
      DOM.queueUpdate(() => this.classList.toggle("disabled", this.disabled));
    }
    /**
     * @internal
     */
    nameChanged(_previous, _next) {
      if (this.proxy instanceof HTMLElement) {
        this.proxy.name = this.name;
      }
    }
    /**
     * @internal
     */
    requiredChanged(_previous, _next) {
      if (this.proxy instanceof HTMLElement) {
        this.proxy.required = this.required;
      }
      DOM.queueUpdate(() => this.classList.toggle("required", this.required));
      this.validate();
    }
    /**
     * The element internals object. Will only exist
     * in browsers supporting the attachInternals API
     * @internal
     */
    get elementInternals() {
      if (!supportsElementInternals) {
        return null;
      }
      let internals = InternalsMap.get(this);
      if (!internals) {
        internals = this.attachInternals();
        InternalsMap.set(this, internals);
      }
      return internals;
    }
    /**
     * @internal
     */
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener("keypress", this._keypressHandler);
      if (!this.value) {
        this.value = this.initialValue;
        this.dirtyValue = false;
      }
      if (!this.elementInternals) {
        this.attachProxy();
        if (this.form) {
          this.form.addEventListener("reset", this.formResetCallback);
        }
      }
    }
    /**
     * @internal
     */
    disconnectedCallback() {
      super.disconnectedCallback();
      this.proxyEventsToBlock.forEach(
        (name) => this.proxy.removeEventListener(name, this.stopPropagation)
      );
      if (!this.elementInternals && this.form) {
        this.form.removeEventListener("reset", this.formResetCallback);
      }
    }
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.elementInternals ? this.elementInternals.checkValidity() : this.proxy.checkValidity();
    }
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.elementInternals ? this.elementInternals.reportValidity() : this.proxy.reportValidity();
    }
    /**
     * Set the validity of the control. In cases when the elementInternals object is not
     * available (and the proxy element is used to report validity), this function will
     * do nothing unless a message is provided, at which point the setCustomValidity method
     * of the proxy element will be invoked with the provided message.
     * @param flags - Validity flags
     * @param message - Optional message to supply
     * @param anchor - Optional element used by UA to display an interactive validation UI
     * @internal
     */
    setValidity(flags, message, anchor) {
      if ("valid" in flags && this.disabled) {
        const { valid, ...cleanFlags } = flags;
        flags = cleanFlags;
      }
      if (this.elementInternals) {
        this.elementInternals.setValidity(flags, message, anchor);
      } else if (typeof message === "string") {
        this.proxy.setCustomValidity(message);
      }
    }
    /**
     * Invoked when a connected component's form or fieldset has its disabled
     * state changed.
     * @param disabled - the disabled value of the form / fieldset
     * @internal
     */
    formDisabledCallback(disabled) {
      this.disabled = disabled;
    }
    /**
     * @internal
     */
    formResetCallback() {
      this.value = this.initialValue;
      this.dirtyValue = false;
    }
    /**
     * Attach the proxy element to the DOM
     * @internal
     */
    attachProxy() {
      if (!this.proxyInitialized) {
        this.proxyInitialized = true;
        this.proxy.style.display = "none";
        this.proxyEventsToBlock.forEach(
          (name) => this.proxy.addEventListener(name, this.stopPropagation)
        );
        this.proxy.disabled = this.disabled;
        this.proxy.required = this.required;
        if (typeof this.name === "string") {
          this.proxy.name = this.name;
        }
        if (typeof this.value === "string") {
          this.proxy.value = this.value;
        }
        this.proxy.setAttribute("slot", proxySlotName);
        this.proxySlot = document.createElement("slot");
        this.proxySlot.setAttribute("name", proxySlotName);
      }
      this.shadowRoot.appendChild(this.proxySlot);
      this.appendChild(this.proxy);
    }
    /**
     * Detach the proxy element from the DOM
     * @internal
     */
    detachProxy() {
      this.removeChild(this.proxy);
      this.shadowRoot.removeChild(this.proxySlot);
    }
    /**
     * Sets the validity of the custom element. By default this uses the proxy element to determine
     * validity, but this can be extended or replaced in implementation.
     *
     * @param anchor - The anchor element to provide to ElementInternals.setValidity for surfacing the browser's constraint validation UI
     * @internal
     */
    validate(anchor) {
      if (this.proxy instanceof HTMLElement && this.elementInternals) {
        const isValid = this.proxy.validity.valid;
        const control = this.control;
        const controlIsInvalidDueToMinOrMaxLength = control && control.validity && !control.validity.valid && (control.validity.tooShort || control.validity.tooLong);
        if (isValid && controlIsInvalidDueToMinOrMaxLength) {
          this.setValidity(control.validity, control.validationMessage, anchor);
        } else {
          this.setValidity(
            this.proxy.validity,
            this.proxy.validationMessage,
            anchor
          );
        }
      }
    }
    /**
     * Associates the provided value (and optional state) with the parent form.
     * @param value - The value to set
     * @param state - The state object provided to during session restores and when autofilling.
     * @internal
     */
    setFormValue(value, state) {
      if (this.elementInternals) {
        this.elementInternals.setFormValue(value, state || value);
      }
    }
    /**
     * @internal
     */
    _keypressHandler(e) {
      switch (e.key) {
        case keyEnter:
          if (this.form instanceof HTMLFormElement) {
            const defaultButton = this.form.querySelector(
              "[type=submit]"
            );
            defaultButton?.click();
          }
          break;
      }
    }
    /**
     * Used to stop propagation of proxy element events
     * @param e - Event object
     * @internal
     */
    stopPropagation(e) {
      e.stopPropagation();
    }
  }
  __decorateClass([
    attr({ attribute: "current-value" })
  ], FormAssociatedElement.prototype, "value");
  __decorateClass([
    attr({ mode: "fromView", attribute: "value" })
  ], FormAssociatedElement.prototype, "initialValue");
  __decorateClass([
    attr({ mode: "boolean" })
  ], FormAssociatedElement.prototype, "disabled");
  __decorateClass([
    attr
  ], FormAssociatedElement.prototype, "name");
  __decorateClass([
    attr({ mode: "boolean" })
  ], FormAssociatedElement.prototype, "required");
  return FormAssociatedElement;
};
const currentCheckedConverter = {
  fromView(value) {
    if (typeof value === "string") {
      return value.toLowerCase() === "true";
    }
    return !!value;
  },
  toView(value) {
    return value ? "true" : "false";
  }
};
const CheckableFormAssociated = (Base) => {
  class CheckableFormAssociatedElement extends FormAssociated(Base) {
    constructor() {
      super(...arguments);
      /**
       * Tracks whether the "checked" property has been changed.
       * This is necessary to provide consistent behavior with
       * normal input checkboxes
       * @internal
       */
      this.dirtyChecked = false;
      this.defaultChecked = false;
    }
    /**
     * @internal
     */
    defaultCheckedChanged() {
      if (!this.dirtyChecked) {
        this.checked = this.defaultChecked;
        this.dirtyChecked = false;
      }
    }
    /**
     * @internal
     */
    checkedChanged(prev, _next) {
      if (!this.dirtyChecked) {
        this.dirtyChecked = true;
      }
      this.updateForm();
      if (this.proxy instanceof HTMLInputElement) {
        this.proxy.checked = this.checked;
      }
      if (prev !== void 0) {
        this.$emit("change");
      }
      this.validate();
    }
    /**
     * @deprecated Use `defaultChecked` instead.
     */
    get checkedAttribute() {
      return this.defaultChecked;
    }
    set checkedAttribute(value) {
      this.defaultChecked = value;
    }
    /**
     * @deprecated Use `checked` instead.
     */
    get currentChecked() {
      return this.checked;
    }
    set currentChecked(value) {
      this.checked = value;
    }
    /**
     * @internal
     */
    updateForm() {
      const value = this.checked ? this.value : null;
      this.setFormValue(value, value);
    }
    /**
     * @internal
     */
    connectedCallback() {
      super.connectedCallback();
      this.updateForm();
    }
    /**
     * @internal
     */
    formResetCallback() {
      super.formResetCallback();
      this.checked = !!this.defaultChecked;
      this.dirtyChecked = false;
    }
  }
  __decorateClass([
    attr({
      attribute: "checked",
      mode: "boolean"
    })
  ], CheckableFormAssociatedElement.prototype, "defaultChecked");
  __decorateClass([
    attr({ attribute: "current-checked", converter: currentCheckedConverter })
  ], CheckableFormAssociatedElement.prototype, "checked");
  return CheckableFormAssociatedElement;
};

export { CheckableFormAssociated as C, FormAssociated as F };
